import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';

type AppRoot = {
  app: { socketUrl: string | null };
};

type SocketContextValue = {
  socket: Socket | null;
  connected: boolean;
};

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  connected: false,
});

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}

type Props = { children: ReactNode };

// Connection plumbing only — backend emits nothing yet; never blocks the tree.
export function SocketProvider({ children }: Props) {
  const socketUrl = useSelector((s: AppRoot) => s.app.socketUrl);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!socketUrl) return;
    const next = io(socketUrl, { autoConnect: true, transports: ['websocket'] });
    next.on('connect', () => setConnected(true));
    next.on('disconnect', () => setConnected(false));
    setSocket(next);
    return () => {
      next.removeAllListeners();
      next.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [socketUrl]);

  return <SocketContext.Provider value={{ socket, connected }}>{children}</SocketContext.Provider>;
}
